import { baseUrl } from '../baseUrl';
import { createPkcePair, createState } from './pkce';

const VERIFIER_STORAGE_KEY = 'ontology-editor.pkce-verifier';
const POPUP_FEATURES = 'popup=yes,width=640,height=760';
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;
// Refresh a little early so a request never starts on a token about to die.
const EXPIRY_SKEW_MS = 30 * 1000;

interface CallbackMessage {
  source: 'ontology-editor-oauth';
  code: string | null;
  state: string | null;
  error: string | null;
  errorDescription: string | null;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

interface Session {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

// Memory only. A reload costs a popup round trip, which is the correct trade for
// not leaving an `api`-scoped token in web storage.
let session: Session | null = null;
let pendingAuthorization: Promise<string> | null = null;

export class GitLabAuthError extends Error {}

function getEnv(): Record<string, string | undefined> {
  return (window as unknown as { env?: Record<string, string> }).env ?? {};
}

function getGitLabBaseUrl(): string {
  const host = getEnv().REACT_APP_GITLAB_HOST;

  if (!host || host === 'CHANGEME') {
    throw new GitLabAuthError('GitLab host is not configured');
  }

  return `https://${host}`;
}

function getClientId(): string {
  const clientId = getEnv().REACT_APP_GITLAB_CLIENT_ID;

  if (!clientId || clientId === 'CHANGEME') {
    throw new GitLabAuthError('GitLab OAuth client id is not configured');
  }

  return clientId;
}

/**
 * The callback page is served by our own Express runtime, which is the origin the
 * microfrontend bundle was loaded from — not the Witboost page hosting us.
 */
function getRedirectUri(): string {
  return `${new URL(baseUrl).origin}/oauth-callback`;
}

function isCallbackMessage(data: unknown): data is CallbackMessage {
  return typeof data === 'object'
    && data !== null
    && (data as CallbackMessage).source === 'ontology-editor-oauth';
}

function storeSession(token: TokenResponse): string {
  session = {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    expiresAt: Date.now() + (token.expires_in ?? 7200) * 1000,
  };

  return session.accessToken;
}

async function requestToken(body: Record<string, string>): Promise<TokenResponse> {
  const response = await fetch(`${getGitLabBaseUrl()}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new GitLabAuthError(`Token request failed with ${response.status}`);
  }

  return await response.json() as TokenResponse;
}

function waitForCode(popup: Window, expectedState: string, redirectUri: string): Promise<string> {
  const expectedOrigin = new URL(redirectUri).origin;

  return new Promise((resolve, reject) => {
    const settle = (finish: () => void) => {
      window.removeEventListener('message', onMessage);
      clearInterval(closedTimer);
      clearTimeout(timeoutTimer);
      finish();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin || !isCallbackMessage(event.data)) {
        return;
      }

      const { code, state, error, errorDescription } = event.data;

      if (error) {
        settle(() => reject(new GitLabAuthError(errorDescription || error)));
        return;
      }

      if (state !== expectedState) {
        settle(() => reject(new GitLabAuthError('OAuth state mismatch')));
        return;
      }

      if (!code) {
        settle(() => reject(new GitLabAuthError('Authorization code missing')));
        return;
      }

      settle(() => resolve(code));
    };

    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        settle(() => reject(new GitLabAuthError('Sign-in window was closed')));
      }
    }, 500);

    const timeoutTimer = window.setTimeout(() => {
      settle(() => reject(new GitLabAuthError('Sign-in timed out')));
    }, POPUP_TIMEOUT_MS);

    window.addEventListener('message', onMessage);
  });
}

async function authorizeInPopup(): Promise<string> {
  const { verifier, challenge } = await createPkcePair();
  const state = createState();
  const redirectUri = getRedirectUri();

  // Single use and consumed by the very next exchange, so session storage is fine.
  window.sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);

  const authorizeUrl = new URL(`${getGitLabBaseUrl()}/oauth/authorize`);
  authorizeUrl.search = new URLSearchParams({
    client_id: getClientId(),
    redirect_uri: redirectUri,
    response_type: 'code',
    state,
    scope: 'api',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  }).toString();

  const popup = window.open(authorizeUrl.toString(), 'gitlab-oauth', POPUP_FEATURES);

  if (!popup) {
    throw new GitLabAuthError('Sign-in popup was blocked by the browser');
  }

  try {
    const code = await waitForCode(popup, state, redirectUri);

    return storeSession(await requestToken({
      client_id: getClientId(),
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    }));
  } finally {
    window.sessionStorage.removeItem(VERIFIER_STORAGE_KEY);

    if (!popup.closed) {
      popup.close();
    }
  }
}

/** Deduplicated so concurrent callers share one popup. */
function authorize(): Promise<string> {
  if (!pendingAuthorization) {
    pendingAuthorization = authorizeInPopup().finally(() => {
      pendingAuthorization = null;
    });
  }

  return pendingAuthorization;
}

async function refresh(): Promise<string | null> {
  if (!session?.refreshToken) {
    return null;
  }

  try {
    // GitLab rotates refresh tokens for public clients, so the response replaces both.
    return storeSession(await requestToken({
      client_id: getClientId(),
      grant_type: 'refresh_token',
      refresh_token: session.refreshToken,
    }));
  } catch {
    session = null;
    return null;
  }
}

async function getAccessToken(): Promise<string> {
  if (session && Date.now() < session.expiresAt - EXPIRY_SKEW_MS) {
    return session.accessToken;
  }

  return (session ? await refresh() : null) ?? await authorize();
}

export function isSignedIn(): boolean {
  return session !== null;
}

export function signOut(): void {
  session = null;
}

/**
 * Runs an authenticated GitLab call, reacting to expiry rather than watching the
 * clock: refresh first, popup second, and only ever one retry.
 */
export async function withAuth(call: (accessToken: string) => Promise<Response>): Promise<Response> {
  const response = await call(await getAccessToken());

  if (response.status !== 401) {
    return response;
  }

  const renewed = await refresh() ?? await authorize();
  return await call(renewed);
}
